import {Color} from '@benchling/nucleus-ui';
import {Button, createStyles, Menu, Stack, Text} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React, {useRef} from 'react';

import {AssistantEffort} from 'llm/chat-library/ChatDAO';
import {CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX} from 'llm/chat-library/ui/AIStyleConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface AssistantModeDropdownProps {
  effort?: AssistantEffort;
  onChange: (effort: AssistantEffort) => void;
  disabled: boolean;
}

const useStyles = createStyles((theme) => ({
  trigger: {
    height: CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX,
    color: Color.GRAY_500,
    fontWeight: 400,
    padding: `0 ${theme.spacing.sm}`,
  },
  chevron: {
    color: Color.GRAY_500,
    width: theme.spacing.sm,
    height: theme.spacing.sm,
  },
  dropdown: {
    border: `1px solid ${Color.GRAY_300}`,
    boxShadow: '0 3px 12px 0 rgb(0 0 0 / 6%)',
    '&&': {
      // The menu theme sets padding with !important.
      padding: `${theme.spacing.sm} !important`,
    },
  },
  option: {
    width: 'auto',
    borderRadius: theme.radius.md,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
  },
  label: {
    whiteSpace: 'nowrap',
  },
  check: {
    color: Color.BLUE_500,
    alignSelf: 'flex-start',
    marginLeft: theme.spacing.md,
    marginRight: theme.spacing.xs,
    marginTop: theme.spacing.xs,
    fontSize: theme.fontSizes.sm,
  },
  hiddenCheck: {
    visibility: 'hidden',
  },
}));

export default function AssistantModeDropdown({
  effort = AssistantEffort.STANDARD,
  onChange,
  disabled,
}: AssistantModeDropdownProps): JSX.Element {
  const {classes} = useStyles();
  const selectedModeDescriptionId = useId();
  const standardDescriptionId = useId();
  const quickDescriptionId = useId();
  const selectedMode = effort === AssistantEffort.QUICK ? 'Quick' : 'Standard';
  const selectedOptionRef = useRef<HTMLButtonElement>(null);
  const handleDropdownFocus = (event: React.FocusEvent<HTMLDivElement>): void => {
    if (event.target instanceof HTMLElement && event.target.hasAttribute('data-menu-dropdown')) {
      selectedOptionRef.current?.focus({preventScroll: true});
    }
  };
  const handleSelectStandard = (): void => {
    onChange(AssistantEffort.STANDARD);
  };
  const handleSelectQuick = (): void => {
    onChange(AssistantEffort.QUICK);
  };

  return (
    <Menu
      classNames={{itemLabel: classes.label, itemRightSection: classes.check}}
      position="top-end"
      width="max-content"
      radius="lg"
      returnFocus={true}
      withArrow={false}
      withinPortal={true}
      offset={4}
    >
      <Menu.Target>
        <Button
          aria-label="Response mode"
          aria-describedby={selectedModeDescriptionId}
          className={classes.trigger}
          disabled={disabled}
          rightIcon={<BenchlingIcon icon="chevron-down" className={classes.chevron} />}
          variant="ghost"
        >
          <span id={selectedModeDescriptionId}>{selectedMode}</span>
        </Button>
      </Menu.Target>
      <Menu.Dropdown className={classes.dropdown} onFocusCapture={handleDropdownFocus}>
        <Stack spacing="none" align="stretch">
          <Menu.Item
            aria-label="Standard"
            aria-describedby={standardDescriptionId}
            aria-current={effort === AssistantEffort.STANDARD ? true : undefined}
            className={classes.option}
            disabled={disabled}
            onClick={handleSelectStandard}
            ref={effort === AssistantEffort.STANDARD ? selectedOptionRef : undefined}
            rightSection={
              <BenchlingIcon
                icon="check"
                aria-hidden={true}
                className={effort === AssistantEffort.STANDARD ? undefined : classes.hiddenCheck}
              />
            }
          >
            <Stack spacing="xs">
              <Text size="md">Standard</Text>
              <Text id={standardDescriptionId} size="md" color={Color.GRAY_500}>
                Answer complex questions
              </Text>
            </Stack>
          </Menu.Item>
          <Menu.Item
            aria-label="Quick"
            aria-describedby={quickDescriptionId}
            aria-current={effort === AssistantEffort.QUICK ? true : undefined}
            className={classes.option}
            disabled={disabled}
            onClick={handleSelectQuick}
            ref={effort === AssistantEffort.QUICK ? selectedOptionRef : undefined}
            rightSection={
              <BenchlingIcon
                icon="check"
                aria-hidden={true}
                className={effort === AssistantEffort.QUICK ? undefined : classes.hiddenCheck}
              />
            }
          >
            <Stack spacing="xs">
              <Text size="md">Quick</Text>
              <Text id={quickDescriptionId} size="md" color={Color.GRAY_500}>
                Find answers fast
              </Text>
            </Stack>
          </Menu.Item>
        </Stack>
      </Menu.Dropdown>
    </Menu>
  );
}
